import React, { useState } from "react";
import {
  LogIn,
  UserPlus,
  Mail,
  Lock,
  User,
  Building2,
  Phone,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  X,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import api, { tokenStorage } from "../api";
import { useAuth } from "../contexts/AuthContext";

/**
 * CustomerAuthModal
 * Interactive AJAX/jQuery style customer login and registration component.
 * Can be rendered as an inline expandable panel or as a standalone modal.
 * 100% Mobile & Tablet Responsive.
 */
export default function CustomerAuthModal({
  isOpen = true,
  onClose,
  onSuccess,
  isModal = false,
  title = "Kundenbereich – Anmeldung & Registrierung",
  subtitle = "Bitte melden Sie sich an oder erstellen Sie ein Kundenkonto, um fortzufahren.",
}) {
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register form state
  const [regFirstName, setRegFirstName] = useState("");
  const [regLastName, setRegLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regCompany, setRegCompany] = useState("");
  const [regPhone, setRegPhone] = useState("");

  const { customerLogin, customerRegister } = useAuth();

  if (!isOpen) return null;

  // AJAX Login Handler
  const handleAjaxLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!loginEmail.trim() || !loginPassword) {
      setError("Bitte E-Mail-Adresse und Passwort eingeben.");
      return;
    }

    setLoading(true);
    try {
      let loggedUser;
      if (customerLogin) {
        loggedUser = await customerLogin(loginEmail.trim(), loginPassword);
      } else {
        const res = await api.post("/auth/login", {
          email: loginEmail.trim(),
          password: loginPassword,
        });
        const token = res.data.access_token || res.data.token;
        if (token) tokenStorage.set(token, true);
        loggedUser = res.data.user || res.data;
      }

      setSuccessMsg("Erfolgreich als Kunde angemeldet!");
      setTimeout(() => {
        if (onSuccess) onSuccess(loggedUser);
      }, 500);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.displayMessage ||
        err.message ||
        "Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // AJAX Register Handler
  const handleAjaxRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!regFirstName.trim() || !regLastName.trim() || !regEmail.trim() || !regPassword) {
      setError("Bitte füllen Sie alle erforderlichen Pflichtfelder (*) aus.");
      return;
    }

    if (regPassword.length < 6) {
      setError("Das Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }

    setLoading(true);
    try {
      const regData = {
        firstName: regFirstName.trim(),
        lastName: regLastName.trim(),
        email: regEmail.trim(),
        password: regPassword,
        company: regCompany.trim() || undefined,
        phone: regPhone.trim() || undefined,
      };

      let newUser;
      if (customerRegister) {
        newUser = await customerRegister(regData);
      } else {
        const res = await api.post("/auth/register", regData);
        const token = res.data.access_token || res.data.token;
        if (token) tokenStorage.set(token, true);
        newUser = res.data.user || res.data;
      }

      setSuccessMsg("Kundenkonto erfolgreich erstellt und eingeloggt!");
      setTimeout(() => {
        if (onSuccess) onSuccess(newUser);
      }, 500);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.displayMessage ||
        err.message ||
        "Registrierung fehlgeschlagen. Bitte überprüfen Sie Ihre Angaben.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="w-full rounded-2xl sm:rounded-3xl bg-gradient-to-b from-[#121724] via-[#0E121D] to-[#0A0D15] border border-white/15 text-white p-4 sm:p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
      {/* Header */}
      <div className="flex items-start justify-between pb-3 sm:pb-4 border-b border-white/10 gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-[#E63946]/20 text-[#E63946] border border-[#E63946]/30 shrink-0">
            <ShieldCheck size={20} />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-[#FF7A00]">
                SCHWEIZER KUNDENPORTAL
              </span>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white leading-tight">{title}</h3>
            {subtitle && (
              <p className="text-[11px] sm:text-xs text-slate-300 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-slate-400 hover:text-white transition shrink-0 cursor-pointer"
            aria-label="Schliessen"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Tab Switcher (AJAX Style) */}
      <div className="mt-4 grid grid-cols-2 gap-2 p-1 bg-black/40 rounded-xl border border-white/10 text-xs font-bold">
        <button
          type="button"
          onClick={() => {
            setMode("login");
            setError("");
            setSuccessMsg("");
          }}
          className={`py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition cursor-pointer ${
            mode === "login"
              ? "bg-[#E63946] text-white shadow-md font-black"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <LogIn size={14} />
          <span>Kunden-Login</span>
        </button>

        <button
          type="button"
          onClick={() => {
            setMode("register");
            setError("");
            setSuccessMsg("");
          }}
          className={`py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition cursor-pointer ${
            mode === "register"
              ? "bg-[#E63946] text-white shadow-md font-black"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <UserPlus size={14} />
          <span>Neu registrieren</span>
        </button>
      </div>

      {/* Feedback Alerts */}
      {error && (
        <div className="mt-3.5 p-3 rounded-xl bg-red-950/60 border border-red-500/50 text-red-200 text-xs flex items-center gap-2 animate-fadeIn">
          <AlertCircle size={15} className="text-red-400 shrink-0" />
          <span className="leading-snug">{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="mt-3.5 p-3 rounded-xl bg-emerald-950/60 border border-emerald-500/50 text-emerald-200 text-xs flex items-center gap-2 animate-fadeIn">
          <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />
          <span className="font-bold">{successMsg}</span>
        </div>
      )}

      {/* FORM 1: LOGIN */}
      {mode === "login" && (
        <form onSubmit={handleAjaxLogin} className="mt-4 space-y-3">
          <div>
            <label className="text-[11px] font-bold text-slate-300 block mb-1">
              E-Mail-Adresse *
            </label>
            <div className="relative">
              <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                required
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="ihre.adresse@firma.ch"
                className="w-full pl-10 pr-3 py-2.5 sm:py-3 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946] transition"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold text-slate-300 block mb-1">Passwort *</label>
            <div className="relative">
              <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="password"
                required
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-3 py-2.5 sm:py-3 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946] transition"
              />
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#E63946] to-[#d62839] hover:from-[#d62839] hover:to-[#c1121f] text-white font-black text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg shadow-red-600/30 transition disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  <span>Wird verifiziert...</span>
                </>
              ) : (
                <>
                  <LogIn size={15} />
                  <span>Als Kunde anmelden & fortfahren</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* FORM 2: REGISTER */}
      {mode === "register" && (
        <form onSubmit={handleAjaxRegister} className="mt-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold text-slate-300 block mb-1">Vorname *</label>
              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  required
                  value={regFirstName}
                  onChange={(e) => setRegFirstName(e.target.value)}
                  placeholder="Max"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-300 block mb-1">Nachname *</label>
              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  required
                  value={regLastName}
                  onChange={(e) => setRegLastName(e.target.value)}
                  placeholder="Muster"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold text-slate-300 block mb-1">
              E-Mail-Adresse (Login & Rechnungsversand) *
            </label>
            <div className="relative">
              <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                required
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder="max.muster@firma.ch"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold text-slate-300 block mb-1">
              Passwort erstellen (min. 6 Zeichen) *
            </label>
            <div className="relative">
              <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="password"
                required
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold text-slate-300 block mb-1">
                Firma (Optional)
              </label>
              <div className="relative">
                <Building2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={regCompany}
                  onChange={(e) => setRegCompany(e.target.value)}
                  placeholder="Muster AG"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-300 block mb-1">
                Telefon (Optional)
              </label>
              <div className="relative">
                <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={regPhone}
                  onChange={(e) => setRegPhone(e.target.value)}
                  placeholder="+41 76 000 00 00"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#E63946]"
                />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#E63946] to-[#FF7A00] hover:from-[#c5303d] hover:to-[#e56d00] text-white font-black text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg shadow-orange-600/25 transition disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  <span>Konto wird erstellt...</span>
                </>
              ) : (
                <>
                  <Sparkles size={15} />
                  <span>Konto erstellen & fortfahren</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Privacy note */}
      <p className="mt-3 text-[10px] text-center text-slate-400">
        🔒 Ihre Daten werden nach Schweizer Datenschutzgesetz (DSG) verschlüsselt übertragen.
      </p>
    </div>
  );

  if (isModal) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
        onClick={onClose}
      >
        <div
          className="max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      </div>
    );
  }

  return content;
}
